import { defineStore } from "pinia";

import { Entity } from "@/enums/entityProfiles";
import relationsApiService from "@/services/modules/profiles/relations";

import type { OwnershipSnapshot, SnapshotOwner } from "@/models/entityProfiles";
import type {
  BaseProfileRelationship,
  CreateEntitySnapshotPayload,
} from "@/models/entityProfiles";
import type { RelationshipPayload } from "@/models/entityProfiles";

interface UseRelationsState {
  relations: BaseProfileRelationship | null;
  selectedRelations: BaseProfileRelationship | null;
  latestOwnershipSnapshot: OwnershipSnapshot | null;
}

const state = (): UseRelationsState => ({
  relations: null,
  selectedRelations: null,
  latestOwnershipSnapshot: null,
});

const useEntityRelationsStore = defineStore("entityRelations", {
  state,
  actions: {
    async getRelations(payload: { type: Entity; id: string }) {
      const data = await relationsApiService.getRelations(
        payload.type,
        payload.id
      );

      this.relations = data;
      return data;
    },

    async selectRelations(payload: { type: Entity; id: string }) {
      const data = await relationsApiService.getRelations(
        payload.type,
        payload.id
      );

      this.selectedRelations = data;
    },

    async getLatestBusinessSnapshot(businessEntityId: string) {
      const data = await relationsApiService.getLatestEntityOwnershipSnapshot(
        businessEntityId,
        Entity.business
      );
      this.latestOwnershipSnapshot = data?.id ? data : null;
    },

    async createOwnershipSnapshot({
      entityId,
      entityType,
      data,
    }: {
      entityId: string;
      entityType: Entity;
      data: CreateEntitySnapshotPayload;
    }) {
      const newSnapshot =
        await relationsApiService.createEntityOwnershipSnapshot(
          entityId,
          entityType,
          data
        );
      this.latestOwnershipSnapshot = newSnapshot;
    },

    async addRelation({
      profileId,
      initiatingEntityType,
      relationship,
      updateRelationship = true,
    }: {
      profileId: string;
      initiatingEntityType: Entity;
      relationship: RelationshipPayload;
      updateRelationship: boolean;
    }) {
      const updatedRelationship = await relationsApiService.createRelationships(
        profileId,
        initiatingEntityType,
        relationship
      );
      if (updateRelationship) {
        this.selectedRelations = updatedRelationship;
        this.relations = updatedRelationship;
      }
    },

    async removeRelation({
      profileId,
      relationship,
    }: {
      profileId: string;
      relationship: RelationshipPayload;
    }) {
      await relationsApiService.removeRelationships(profileId, relationship);
    },
  },
  getters: {
    latestOwnershipSnapshotOwners({ latestOwnershipSnapshot }) {
      if (!latestOwnershipSnapshot?.owners.length) {
        return [];
      }

      return latestOwnershipSnapshot.owners.map((owner: SnapshotOwner) => ({
        percentage: owner.percentage,
        profile_id: owner.id,
      }));
    },
  },
});

export default useEntityRelationsStore;
